// La sección Instagram de la sidebar — ADR-2026-10-06-instagram-tiene-su-propia-seccion.
//
// Entra directo al calendario de la marca: sin listado ni "Nuevo lanzamiento" de por
// medio. Por dentro sigue siendo un lanzamiento `redes`
// (ADR-2026-09-15-un-lanzamiento-de-redes-es-un-calendario-de-posts), pero eso ya no
// se ve: hay uno por marca y se crea solo la primera vez.
//
// Arriba a la derecha, "Calendario | Métricas": el dashboard de cómo le va a la cuenta
// (ADR-2026-10-29-dashboard-de-metricas-de-instagram). La sección va en la URL (`?ver=metricas`)
// para poder mandar a alguien directo ahí.
import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AlertCircle, BarChart3, CalendarDays, Instagram, Loader2 } from 'lucide-react';
import { useBrandProfile } from '../../../hooks/useBrandProfile';
import { useUserGroup } from '../../../hooks/useUserGroup';
import { reglasDe } from '../canales';
import { seVenLasMetricas } from './avisos';
import { CalendarioDeRedes } from './CalendarioDeRedes';
import { MetricasDeInstagram } from './metricas/MetricasDeInstagram';
import { SelectorSegmentado } from './metricas/SelectorSegmentado';
import { PublicarSolo } from './PublicarSolo';
import { calendarioDeLaMarca, type CalendarioDeLaMarca } from './postsApi';

type Seccion = 'calendario' | 'metricas';

export default function CalendarioDeInstagram() {
  const { profile, currentBrandId, loading: cargandoMarca } = useBrandProfile();
  const companyId = profile?.id_company ?? null;

  const [calendario, setCalendario] = useState<CalendarioDeLaMarca | null>(null);
  const [cargando, setCargando] = useState(true);
  /** Con un post abierto el encabezado es el del editor ("Nuevo post"): éste hablaría del mes. */
  const [editando, setEditando] = useState(false);

  const { isDevTester } = useUserGroup();
  const conMetricas = seVenLasMetricas(isDevTester);
  const [params, setParams] = useSearchParams();
  const seccion: Seccion = conMetricas && params.get('ver') === 'metricas' ? 'metricas' : 'calendario';
  /** El post que se eligió en las métricas con "Abrir en el calendario". Es de UNA marca. */
  const [postInicial, setPostInicial] = useState<string | null>(null);
  useEffect(() => setPostInicial(null), [currentBrandId]);
  const cambiarSeccion = useCallback(
    (s: Seccion) => {
      if (s === 'metricas') setPostInicial(null);
      setParams(
        (antes) => {
          const p = new URLSearchParams(antes);
          if (s === 'metricas') p.set('ver', 'metricas');
          else p.delete('ver');
          return p;
        },
        { replace: true },
      );
    },
    [setParams],
  );

  /** El último pedido: si se cambia de marca con uno en vuelo, el de la marca vieja no pisa. */
  const pedidoRef = useRef(0);
  const cargar = useCallback(async () => {
    if (!companyId) return;
    const n = ++pedidoRef.current;
    const c = await calendarioDeLaMarca(companyId, currentBrandId);
    if (n !== pedidoRef.current) return;
    setCalendario(c);
    setCargando(false);
  }, [companyId, currentBrandId]);

  // Al cambiar de marca se vuelve a "cargando": si no, se ve un instante el calendario
  // de la marca anterior.
  useEffect(() => {
    setCargando(true);
    void cargar();
  }, [cargar]);

  // Sin cartel de "desactivado": la sección es de todos
  // (ADR-2026-10-16-tienda-online-e-instagram-para-todos).
  return (
    <div className="mx-auto max-w-6xl py-4 px-4 md:py-8 md:px-8">
      {/* El `<h1>` es hijo DIRECTO de la fila, hermano del ícono: el aire de arriba del
          shell se lo lleva la fila entera (ADR-2026-09-15-el-titulo-no-se-puede-envolver). */}
      {/* `editando` es del calendario: en Métricas el encabezado se ve siempre. */}
      {(!editando || seccion === 'metricas') && (
        <>
          <div className="flex flex-wrap items-center gap-3">
            <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-gradient-to-tr from-amber-400 via-pink-500 to-purple-600 text-white shadow-sm">
              <Instagram className="w-5 h-5" />
            </span>
            <h1 className="text-xl font-semibold text-brand-black md:text-2xl">Instagram</h1>
            {calendario && !cargando && (
              <div className="ml-auto flex flex-wrap items-center gap-2">
                {conMetricas && (
                  <SelectorSegmentado
                    etiqueta="Sección"
                    opciones={[
                      { valor: 'calendario', texto: 'Calendario', icono: <CalendarDays className="w-3.5 h-3.5" /> },
                      { valor: 'metricas', texto: 'Métricas', icono: <BarChart3 className="w-3.5 h-3.5" /> },
                    ]}
                    valor={seccion}
                    onCambiar={cambiarSeccion}
                  />
                )}
                {/* Avisarme / Publicar automáticamente: sólo dev testers por ahora
                    (ADR-2026-10-28-instagram-publica-el-calendario). Es del calendario. */}
                {seccion === 'calendario' && <PublicarSolo lanzamientoId={calendario.id} brandId={currentBrandId} />}
              </div>
            )}
          </div>
          <p className="mt-1 hidden pl-[52px] text-sm text-neutral-500 md:block">
            {seccion === 'metricas'
              ? 'Cómo le va a tu cuenta y a cada post'
              : calendario && !cargando
              ? `${calendario.posts} post${calendario.posts === 1 ? '' : 's'} planificado${
                  calendario.posts === 1 ? '' : 's'
                } · qué sube, con qué texto y a qué hora`
              : 'Qué sube, con qué texto y a qué hora'}
          </p>
        </>
      )}

      {cargando ? (
        <div className="flex items-center justify-center gap-2 py-24 text-sm text-neutral-500">
          <Loader2 className="w-4 h-4 animate-spin" /> Cargando…
        </div>
      ) : !calendario || !companyId ? (
        <div className="mx-auto max-w-lg py-24 px-6 text-center">
          <AlertCircle className="mx-auto w-8 h-8 text-neutral-300" />
          <p className="mt-4 text-sm text-brand-black">No pudimos abrir tu calendario</p>
          <button
            type="button"
            onClick={() => {
              setCargando(true);
              void cargar();
            }}
            className="mt-4 text-sm text-neutral-500 underline"
          >
            Reintentar
          </button>
        </div>
      ) : seccion === 'metricas' ? (
        // Hasta saber la marca activa no se lee Instagram: con la marca todavía en null, la edge
        // leería la de por defecto y después otra vez la real (dos veces el cupo de Meta).
        cargandoMarca ? (
          <div className="flex items-center justify-center gap-2 py-24 text-sm text-neutral-500">
            <Loader2 className="w-4 h-4 animate-spin" /> Cargando…
          </div>
        ) : (
        <MetricasDeInstagram
          brandId={currentBrandId}
          companyId={companyId}
          lanzamientoId={calendario.id}
          onAbrirEnCalendario={(id) => {
            cambiarSeccion('calendario');
            setPostInicial(id);
          }}
        />
        )
      ) : (
        <CalendarioDeRedes
          lanzamientoId={calendario.id}
          companyId={companyId}
          fechaLanzamiento={calendario.fechaLanzamiento}
          aceptaVideo={reglasDe('redes').conVideo}
          onCambio={() => void cargar()}
          onEditando={setEditando}
          postInicial={postInicial}
        />
      )}
    </div>
  );
}
